import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { environment } from '@/config/environment';

export interface GradingFilter {
  columnName: string;
  value: string;
}

export interface GradingSort extends GradingFilter {}

export interface InventoryGradingRequest {
  unitOfMeasure: string;
  filters?: GradingFilter[] | null;
  searchFilters?: string | null;
  sorting?: GradingSort[] | null;
  pageNumber?: number | null;
  linesPerPage?: number | null;
}

export interface InventoryGradingItem extends Record<string, unknown> {
  source?: string;
  materialCode?: string | null;
  materialDescription?: string | null;
  storageLocation?: string | null;
  storageLocationDescription?: string | null;
  loadDate?: string | null;
  railCar?: string | null;
  batch?: string | null;
  unrestrictedSupplyQuantity?: number;
  inQualityInspectionQuantity?: number;
  blockedSupplyQuantity?: number;
  baseUnitOfMeasure?: string | null;
  plant?: string | null;
}

export interface InventoryGradingResponse {
  count?: number;
  data?: InventoryGradingItem[];
}

export const getInventoryGrading = async (request: InventoryGradingRequest, signal?: AbortSignal) => {
  const { data } = await axiosInstance.post<InventoryGradingResponse>(
    '/inventoryGrading', request,
    { headers: jsonHeaders(), signal, timeout: environment.inventoryTimeout },
  );
  return data;
};

export const getInventoryGradingUoms = async (source: string, signal?: AbortSignal) => {
  const pageSize = 1000;
  const uoms: string[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const { data } = await axiosInstance.get<{
      datas: Array<{ uom: string }>;
      totalPages?: number;
      totalCount?: number;
      pageSize?: number;
    }>('/unitofmeasure', {
      params: { PageNumber: pageNumber, PageSize: pageSize, Source: source },
      headers: jsonHeaders(),
      signal,
      timeout: environment.inventoryTimeout,
    });
    uoms.push(...(data.datas ?? []).map((item) => item.uom).filter(Boolean));
    totalPages = Math.max(1, Number(data.totalPages)
      || Math.ceil(Number(data.totalCount ?? 0) / Number(data.pageSize ?? pageSize))
      || 1);
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return uoms;
};

export interface InventoryGradingMutationItem {
  source?: string;
  materialCode?: string | null;
  batch?: string | null;
  plant?: string | null;
  railCar?: string | null;
  blockedSupplyQuantity?: number;
  unrestrictedSupplyQuantity?: number;
  inQualityInspectionQuantity?: number;
  baseUnitOfMeasure?: string | null;
  storageLocation?: string | null;
}

export interface InventoryGradingConversionRequest {
  amount: number;
  item: {
    materialCode: string;
    batch?: string | null;
    plant: string;
    storageLocation?: string | null;
    railCar?: string | null;
    baseUnitOfMeasure: string;
    unrestrictedSupplyQuantity?: number;
    inQualityInspectionQuantity?: number;
    blockedSupplyQuantity?: number;
  };
}

export const convertInventoryGrading = async (
  action: 'block' | 'unrestrict',
  request: InventoryGradingConversionRequest,
) => {
  const { data } = await axiosInstance.post<InventoryGradingItem>(
    `/inventoryGrading/${action}`, {
      ...request,
      item: {
        ...request.item,
        batch: request.item.batch || ' ',
        storageLocation: request.item.storageLocation || ' ',
      },
    },
    { headers: jsonHeaders(), timeout: environment.inventoryTimeout },
  );
  return data;
};

export const updateInventoryGrading = async (item: InventoryGradingMutationItem) => {
  const { data } = await axiosInstance.post<InventoryGradingItem>(
    '/inventoryGrading/update', item,
    { headers: jsonHeaders(), timeout: environment.inventoryTimeout },
  );
  return data;
};
